'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { isKnownImageHost } from '@/lib/image-hosts';

interface BannerData {
  id: string;
  title: string;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  bgColor?: string | null;
  bgGradient?: string | null;
  imageUrl?: string | null;
  textColor: string;
  imageOnly?: boolean;
}

interface HeroCarouselProps {
  banners: BannerData[];
  autoPlayMs?: number;
}

/**
 * Full-width hero carousel with horizontal slide transition.
 * Shows LANDING_HERO banners, or a default promotional slide if none exist.
 *
 * Sizing strategy:
 * - All image-only slides: section uses aspect-[3/1] and the sliding track is
 *   absolutely fills it (`absolute inset-y-0` + `h-full`) so the full landscape
 *   image is always visible — height scales with viewport width, no cropping.
 * - Any text slide: the track is in normal document flow instead (no absolute
 *   fill), so the section's height is driven by the slide content itself.
 *   `min-h-[320px] md:min-h-[480px]` is a FLOOR, not a clip — content taller
 *   than that (heading + subtitle + CTA + trust chips on narrow phones)
 *   expands the section rather than being cut off by overflow-hidden, which
 *   only clips the horizontally-offset off-screen slides.
 */
export function HeroCarousel({ banners, autoPlayMs = 5000 }: HeroCarouselProps) {
  const t = useTranslations('common');
  const slides = banners;
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // When every slide is image-only we can use aspect-ratio sizing (no fixed height needed).
  // If any slide has text, use explicit heights so the text has room to breathe.
  const allImageOnly = slides.every((s) => s.imageOnly);

  const goTo = useCallback((newIndex: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIndex(newIndex);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const prev = useCallback(() => goTo((index - 1 + slides.length) % slides.length), [index, slides.length, goTo]);
  const next = useCallback(() => goTo((index + 1) % slides.length), [index, slides.length, goTo]);

  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(next, autoPlayMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next, autoPlayMs, slides.length]);

  const handleMouseEnter = () => { if (timerRef.current) clearInterval(timerRef.current); };
  const handleMouseLeave = () => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(next, autoPlayMs);
  };

  return (
    <section
      // aspect-[3/1]: on a 390px phone → 130px tall (full landscape image, no crop)
      //               on a 1400px desktop → 467px tall (close to original 480px)
      // min-h-[320px] md:min-h-[480px]: FLOOR for text banners — real height comes
      // from content (see track comment below). overflow-hidden only clips the
      // horizontally-translated off-screen slides, never vertical content.
      className={`relative w-full overflow-hidden ${
        allImageOnly ? 'aspect-[3/1]' : 'min-h-[320px] md:min-h-[480px]'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/*
        Sliding track.
        - Image-only mode: absolutely fills the section so aspect-ratio sizing works.
        - Text mode: kept in normal flow (no absolute inset-y-0 / h-full) so the flex
          row's height — and therefore the section's height — is driven by the tallest
          slide's actual content instead of being clipped to the section's fixed height.
      */}
      <div
        className={`flex transition-transform duration-500 ease-in-out ${
          allImageOnly ? 'absolute inset-y-0 left-0 h-full' : 'relative'
        }`}
        style={{
          width: `${slides.length * 100}%`,
          transform: `translateX(-${(index * 100) / slides.length}%)`,
        }}
      >
        {slides.map((slide, slideIndex) => {
          const color = slide.textColor ?? '#ffffff';
          // Always h2: Hero.tsx above this strip owns the page's one h1, so a text
          // banner must not add a second.
          const HeadingTag = 'h2';

          // Only the gradient/solid fallbacks stay as CSS. An imageUrl renders through
          // <Image> below instead: as a CSS background it bypassed next/image entirely —
          // no AVIF/WebP, no resizing — and prod was shipping a 2.8 MB PNG here.
          const bgStyle: React.CSSProperties = slide.bgGradient
            ? { background: slide.bgGradient }
            : { backgroundColor: slide.bgColor ?? '#0f766e' };

          return (
            <div
              key={slide.id}
              // h-full only matters in image-only mode (track is absolute/h-full there).
              // In text mode the flex row's default align-items: stretch makes every
              // slide match the tallest sibling's content height automatically.
              className={`relative flex-shrink-0 ${allImageOnly ? 'h-full' : ''}`}
              style={{ width: `${100 / slides.length}%` }}
            >
              {/* Background */}
              {slide.imageUrl ? (
                // Every slide is mounted in the transform track, so priority goes to slide 0
                // only — priming all of them would defeat the point. Admin banner imageUrl can
                // be a legacy free-text value, and next/image THROWS at render for a host
                // outside remotePatterns, which would take the homepage hero down; fall back to
                // unoptimized for anything unrecognised. Full-bleed in <main>, hence 100vw.
                <Image
                  src={slide.imageUrl}
                  alt={slide.imageOnly ? slide.title : ''}
                  fill
                  sizes="100vw"
                  className="object-cover object-center"
                  priority={slideIndex === 0}
                  unoptimized={!isKnownImageHost(slide.imageUrl)}
                />
              ) : (
                <div className="absolute inset-0" style={bgStyle} />
              )}

              {/* Overlay only for text-over-image */}
              {slide.imageUrl && !slide.imageOnly && (
                <div className="absolute inset-0 bg-black/40" />
              )}

              {/* Text content — hidden for image-only slides */}
              {!slide.imageOnly && (
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 sm:py-16 md:py-24 min-h-[320px] md:min-h-[480px]">
                  <HeadingTag
                    className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl"
                    style={{ color }}
                  >
                    {slide.title}
                  </HeadingTag>
                  {slide.subtitle && (
                    <p
                      className="mt-3 sm:mt-4 text-sm sm:text-lg md:text-xl max-w-2xl opacity-90 leading-relaxed"
                      style={{ color }}
                    >
                      {slide.subtitle}
                    </p>
                  )}
                  {slide.ctaText && slide.ctaUrl && (
                    <Link
                      href={slide.ctaUrl}
                      className="mt-6 sm:mt-8 inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full font-semibold text-sm sm:text-base transition-all hover:scale-105 hover:shadow-lg active:scale-95"
                      style={{ backgroundColor: color, color: slide.bgColor ?? '#0f766e' }}
                    >
                      {slide.ctaText}
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation arrows: sm+ only. On phones the left arrow sat over the start of headlines
          baked into image-only banners; phones navigate with the dots and autoplay. */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="hidden sm:inline-block absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 transition-colors backdrop-blur-md border border-white/20"
            aria-label={t('prevBanner')}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
          <button
            onClick={next}
            className="hidden sm:inline-block absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 transition-colors backdrop-blur-md border border-white/20"
            aria-label={t('nextBanner')}
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>

          {/* Dots */}
          {/* Each dot sits in a 24px transparent button so the tap target meets WCAG 2.5.8
              (dots are the only manual nav on phones); the visible dot stays the same size.
              Sized in px, not h-6/min-w-6: globals.css sets html to 14px, so 1.5rem is 21px. */}
          <div className="absolute bottom-1 sm:bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="group flex items-center justify-center h-[24px] min-w-[24px] px-1 rounded-full focus-visible:outline-2 focus-visible:outline-white"
                aria-label={t('goSlide', { n: i + 1 })}
                aria-current={i === index ? 'true' : undefined}
              >
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    i === index
                      ? 'w-6 sm:w-8 h-2 sm:h-2.5 bg-white'
                      : 'w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white/40 group-hover:bg-white/60'
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
